'use client';

import { ChevronRight } from 'assets/icons/ChevronRight';
import React, {
  useCallback,
  useState,
  useEffect,
  memo,
  useRef,
} from 'react';
import { IndiaPlan } from './types';
import { SlimCheck } from 'assets/icons';
import IndiaLearnMoreModal from './IndiaLearnMoreModal';
import { useModal } from 'components/shared/Modal';
import { CustomImage } from 'components/shared';
import Image from 'next/image';
import styles from './IndiaPlanCard.module.css';

// Module-level variable to track currently active modal's plan ID
// This ensures only one modal can be active at a time across all cards
let activeModalPlanId: number | null = null;
interface IndiaPlanCardProps {
  plan: IndiaPlan;
  isInCart: boolean;
  onAdd: () => void;
  onRemove: () => void;
  disabled?: boolean; // Disable add button when user hasn't provided pincode
}

const IndiaPlanCardComponent: React.FC<IndiaPlanCardProps> = ({
  plan,
  isInCart,
  onAdd,
  onRemove,
  disabled = false,
}) => {
  const modal = useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Use refs to store callbacks to prevent infinite loop
  // onAdd and onRemove are inline functions that change on every render
  const onAddRef = useRef(onAdd);
  const onRemoveRef = useRef(onRemove);

  // Track if this card's modal is the currently active one
  const isActiveModalRef = useRef(false);

  // Update refs when callbacks change
  useEffect(() => {
    onAddRef.current = onAdd;
    onRemoveRef.current = onRemove;
  }, [onAdd, onRemove]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Extract test count from subtitle (e.g., "60+ Parameters" or "Male • 80+ Parameters" -> "60+" or "80+")
  const extractCountFromSubtitle = (
    subtitle: string | undefined | null,
  ): string => {
    if (!subtitle) return '0+';
    // Match pattern like "60+" or "80+" (with optional + sign) - handles formats like "60+ Parameters" or "Male • 80+ Parameters"
    const match = subtitle.match(/(\d+\+?)/);
    return match ? match[1] : '0+';
  };
  const testCount = extractCountFromSubtitle(plan.subtitle);

  // Store plan in ref to avoid dependency issues while still using latest plan data
  const planRef = useRef(plan);
  useEffect(() => {
    planRef.current = plan;
  }, [plan]);

  // Update modal content when isInCart changes while modal is open
  // Only update if THIS card's modal is the currently active one globally
  useEffect(() => {
    const currentPlan = planRef.current;
    if (
      isModalOpen &&
      isActiveModalRef.current &&
      activeModalPlanId === currentPlan.id
    ) {
      modal.set(
        <IndiaLearnMoreModal
          key={currentPlan.id}
          plan={currentPlan}
          isInCart={isInCart}
          onAdd={() => onAddRef.current()}
          onRemove={() => onRemoveRef.current()}
          onClose={() => {
            setIsModalOpen(false);
            isActiveModalRef.current = false;
            if (activeModalPlanId === currentPlan.id) {
              activeModalPlanId = null;
            }
            modal.clear();
          }}
          disabled={disabled}
        />,
      );
    }
    // Note: modal is excluded from deps to prevent infinite loop
    // plan is accessed via planRef to use latest data without causing re-renders
  }, [isModalOpen, plan.id, isInCart, disabled]);

  // Open modal using ModalContext
  const openModal = useCallback(() => {
    // Set this as the globally active modal
    activeModalPlanId = plan.id;
    isActiveModalRef.current = true;

    // Clear and set content immediately
    modal.clear();
    setIsModalOpen(true);
    modal.set(
      <IndiaLearnMoreModal
        key={plan.id}
        plan={plan}
        isInCart={isInCart}
        onAdd={() => onAddRef.current()}
        onRemove={() => onRemoveRef.current()}
        onClose={() => {
          setIsModalOpen(false);
          isActiveModalRef.current = false;
          if (activeModalPlanId === plan.id) {
            activeModalPlanId = null;
          }
          modal.clear();
        }}
        disabled={disabled}
      />,
    );
    modal.setFullView(); // Full view on mobile
    modal.setCloseBtnHidden(true); // Hide default close button (modal has its own)
    modal.setTop(); // Set highest z-index to appear above header
    modal.setAlignRight(); // Align modal to right with 16px padding on desktop
    modal.show();
  }, [modal, plan, isInCart, disabled]);

  // Handle button click - scroll to pincode if disabled on mobile
  const handleButtonClick = () => {
    if (disabled) {
      // Check if on mobile (viewport width <= 768px)
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // Find the pincode input element
        const pincodeInput = document.getElementById(
          'blood-vision-pincode-input',
        );

        if (pincodeInput) {
          // Scroll to the input with smooth behavior
          pincodeInput.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // Focus the input after a small delay to ensure scroll completes
          setTimeout(() => {
            pincodeInput.focus();
          }, 300);
        }
      }
      return;
    }

    if (isInCart) {
      onRemoveRef.current();
    } else {
      onAddRef.current();
    }
  };

  // Get the first 3 bullet points for description
  const descriptionText = plan.bullet_points
    ?.slice(0, 3)
    .map((point, idx) => <li key={idx}>{point.title}</li>);

  // Determine gender based on suitable_for API data
  const gender = (() => {
    const { male, female } = plan.suitable_for;

    if (male && female) {
      return 'Male, Female';
    } else if (male) {
      return 'Male';
    } else if (female) {
      return 'Female';
    }
    return 'Male, Female'; // Fallback if neither is specified
  })();

  return (
    <>
      <div className={styles.cardContainer}>
        {/* Chevron button for mobile - positioned top right */}
        <button
          className={styles.chevronButton}
          onClick={openModal}
          aria-label="Learn more about this plan"
        >
          <ChevronRight fill="#000000" />
        </button>

        <div className={styles.cardHeader}>
          {/* Thumbnail */}
          <div className={styles.thumbnailContainer}>
            {plan.icon_url ? (
              <Image
                src={plan.icon_url}
                alt={plan.name}
                width={48}
                height={48}
                className="img"
              />
            ) : (
              plan.name.charAt(0).toUpperCase()
            )}
          </div>

          {/* Price Container (Top Right) */}
          <div className={styles.priceContainer}>
            <h3 className={styles.planNameText}>{plan.name}</h3>
            <div className={styles.infoContainer}>
              <span className={styles.testsCount}>{testCount} markers</span>
              <span className={styles.bulletSeparator}>•</span>
              <div className={styles.genderInfoContainer}>
                {gender ? (
                  <CustomImage
                    src={
                      gender === 'Male'
                        ? 'web_v2/blood-vision/icons/male_updated.png'
                        : gender === 'Female'
                        ? 'web_v2/blood-vision/icons/female_updated.png'
                        : 'web_v2/blood-vision/icons/male_female_updated.png'
                    }
                    alt={gender}
                    width={16}
                    height={16}
                    className="img"
                  />
                ) : null}
                <span className={styles.genderText}>{gender}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.middleContainer}>
          {/* Description */}
          <ul className={styles.descriptionList}>{descriptionText}</ul>
          {/* Learn More Link */}
          <button className={styles.learnMoreLink} onClick={openModal}>
            Learn More
            <svg className={styles.plusIcon} viewBox="0 0 16 16" fill="none">
              <circle
                cx="8"
                cy="8"
                r="6.67"
                stroke="#0882FF"
                strokeWidth="1"
                fill="none"
              />
              <path
                d="M8 5.5V10.5M5.5 8H10.5"
                stroke="#0882FF"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {/* Separator */}
        {/* <Separator /> */}

        {/* Bottom Container with Price and Button */}
        <div
          className={styles.bottomContainer}
          data-recommended={plan?.category === 'recommended'}
        >
          <div className={styles.priceSection}>
            <span className={styles.currentPrice}>
              ₹{plan.price_details.price.toLocaleString('en-IN')}
            </span>
            {plan.price_details.discount_applied &&
              plan.price_details.original_price !==
                plan.price_details.price && (
                <span className={styles.originalPrice}>
                  ₹{plan.price_details.original_price.toLocaleString('en-IN')}
                </span>
              )}
          </div>
          <button
            className={styles.addButton}
            data-in-cart={isInCart}
            data-disabled={disabled}
            data-recommended={plan?.category === 'recommended'}
            onClick={handleButtonClick}
            title={disabled ? 'Please enter your pincode to add plans' : ''}
          >
            {isInCart ? (
              <SlimCheck style={{ width: '16.98px', height: '16.98px' }} />
            ) : isMobile || plan?.category !== 'recommended' ? (
              'Add'
            ) : (
              `Book ${plan.name}`
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
// Only re-render if plan id, isInCart status, or disabled state changes
const IndiaPlanCard = memo(IndiaPlanCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.plan.id === nextProps.plan.id &&
    prevProps.isInCart === nextProps.isInCart &&
    prevProps.disabled === nextProps.disabled
  );
});

IndiaPlanCard.displayName = 'IndiaPlanCard';

export default IndiaPlanCard;
